/**
 * React Query hooks for orders search and order detail
 * @module features/orders/queries
 */

import {
	useMutation,
	useQueries,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";

import { apiFetch } from "#/utils/api";

import type {
	OrderDetail,
	OrderSummary,
	ShimLineItemUpdateInput,
	ShimLineItemUpdateResult,
} from "./schema";

type OrderSearchResponse = {
	orders: OrderSummary[];
};

export const orderKeys = {
	all: ["orders"] as const,
	search: (term: string) => [...orderKeys.all, "search", term] as const,
	detail: (id: string) => [...orderKeys.all, "detail", id] as const,
	shipment: (orderId: string, fulfillmentOrderId: string) =>
		[...orderKeys.detail(orderId), "shipment", fulfillmentOrderId] as const,
};

type UpdateOrderMessageInput = {
	orderId: string;
	lineItemId: string;
	message: string;
};

type UpdateOrderMessageResult = {
	success: true;
	orderId: string;
	lineItemId: string;
	message: string;
};

type UpdateOrderFulfillmentDateInput = {
	orderId: string;
	fulfillmentOrderId: string;
	fulfillmentDate: string;
};

type UpdateOrderFulfillmentDateResult = {
	success: true;
	orderId: string;
	fulfillmentOrderId: string;
	fulfillAt: string;
	fulfillBy: string;
	requestedDate: string;
};

type UpdateOrderFulfillAtInput = {
	orderId: string;
	fulfillmentOrderId: string;
	timestamp: string;
};

type UpdateOrderFulfillAtResult = {
	success: true;
	orderId: string;
	fulfillmentOrderId: string;
	fulfillAt: string;
};

type UpdateOrderFulfillByInput = {
	orderId: string;
	fulfillmentOrderId: string;
	timestamp: string;
};

type UpdateOrderFulfillByResult = {
	success: true;
	orderId: string;
	fulfillmentOrderId: string;
	fulfillBy: string;
};

type TriggerOrderScheduleWorkflowInput = {
	orderId: string;
	fulfillmentOrderId: string;
};

type TriggerOrderScheduleWorkflowResult = {
	success: true;
	orderId: string;
	fulfillmentOrderId: string;
	scheduledItems: number;
};

type UpdateOrderShippingLineInput = {
	orderId: string;
};

type UpdateOrderShippingLineResult = {
	success: true;
	orderId: string;
	previousShippingTitle: string | null;
	nextShippingTitle: string;
	physicalItemCount: number;
	totalPriceAmount: string;
	currencyCode: string;
};

/**
 * Searches orders by free-text term.
 */
export async function searchOrdersRequest(term: string): Promise<OrderSummary[]> {
	const response = await apiFetch(`/orders/search?term=${encodeURIComponent(term)}`);
	if (!response.ok) {
		const payload = (await response.json().catch(() => null)) as { error?: string } | null;
		throw new Error(payload?.error || "Failed to search orders");
	}

	const payload = (await response.json()) as OrderSearchResponse;
	return payload.orders || [];
}

/**
 * Fetches one order by Shopify numeric ID or GID.
 */
export async function fetchOrderRequest(id: string): Promise<OrderDetail> {
	const response = await apiFetch(`/orders/${encodeURIComponent(id)}`);
	if (!response.ok) {
		const payload = (await response.json().catch(() => null)) as { error?: string } | null;
		throw new Error(payload?.error || "Failed to fetch order");
	}

	return (await response.json()) as OrderDetail;
}

/**
 * Updates one line item's message in the order message metafield map.
 */
export async function updateOrderMessageRequest(
	input: UpdateOrderMessageInput,
): Promise<UpdateOrderMessageResult> {
	const response = await apiFetch(`/orders/${encodeURIComponent(input.orderId)}/message`, {
		method: "PATCH",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ lineItemId: input.lineItemId, message: input.message }),
	});

	if (!response.ok) {
		const payload = (await response.json().catch(() => null)) as { error?: string } | null;
		throw new Error(payload?.error || "Failed to update message");
	}

	return (await response.json()) as UpdateOrderMessageResult;
}

/**
 * Updates one fulfillment order's date and requested-date metadata.
 */
export async function updateOrderFulfillmentDateRequest(
	input: UpdateOrderFulfillmentDateInput,
): Promise<UpdateOrderFulfillmentDateResult> {
	const response = await apiFetch(`/orders/${encodeURIComponent(input.orderId)}/fulfillment-date`, {
		method: "PATCH",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			fulfillmentOrderId: input.fulfillmentOrderId,
			fulfillmentDate: input.fulfillmentDate,
		}),
	});

	if (!response.ok) {
		const payload = (await response.json().catch(() => null)) as { error?: string } | null;
		throw new Error(payload?.error || "Failed to update fulfillment date");
	}

	return (await response.json()) as UpdateOrderFulfillmentDateResult;
}

/**
 * Updates one fulfillment order's fulfillAt timestamp directly.
 */
export async function updateOrderFulfillAtRequest(
	input: UpdateOrderFulfillAtInput,
): Promise<UpdateOrderFulfillAtResult> {
	const response = await apiFetch(`/orders/${encodeURIComponent(input.orderId)}/fulfill-at`, {
		method: "PATCH",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			fulfillmentOrderId: input.fulfillmentOrderId,
			timestamp: input.timestamp,
		}),
	});

	if (!response.ok) {
		const payload = (await response.json().catch(() => null)) as { error?: string } | null;
		throw new Error(payload?.error || "Failed to update fulfillAt");
	}

	return (await response.json()) as UpdateOrderFulfillAtResult;
}

/**
 * Updates one fulfillment order's fulfillBy timestamp directly.
 */
export async function updateOrderFulfillByRequest(
	input: UpdateOrderFulfillByInput,
): Promise<UpdateOrderFulfillByResult> {
	const response = await apiFetch(`/orders/${encodeURIComponent(input.orderId)}/fulfill-by`, {
		method: "PATCH",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			fulfillmentOrderId: input.fulfillmentOrderId,
			timestamp: input.timestamp,
		}),
	});

	if (!response.ok) {
		const payload = (await response.json().catch(() => null)) as { error?: string } | null;
		throw new Error(payload?.error || "Failed to update fulfillBy");
	}

	return (await response.json()) as UpdateOrderFulfillByResult;
}

/**
 * Triggers scheduling workflow logic for one fulfillment order.
 */
export async function triggerOrderScheduleWorkflowRequest(
	input: TriggerOrderScheduleWorkflowInput,
): Promise<TriggerOrderScheduleWorkflowResult> {
	const response = await apiFetch(
		`/orders/${encodeURIComponent(input.orderId)}/schedule-workflow`,
		{
			method: "PATCH",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				fulfillmentOrderId: input.fulfillmentOrderId,
			}),
		},
	);

	if (!response.ok) {
		const payload = (await response.json().catch(() => null)) as { error?: string } | null;
		throw new Error(payload?.error || "Failed to trigger schedule workflow");
	}

	return (await response.json()) as TriggerOrderScheduleWorkflowResult;
}

/**
 * Toggles order shipping line between standard and guaranteed methods.
 */
export async function updateOrderShippingLineRequest(
	input: UpdateOrderShippingLineInput,
): Promise<UpdateOrderShippingLineResult> {
	const response = await apiFetch(`/orders/${encodeURIComponent(input.orderId)}/shipping-line`, {
		method: "PATCH",
	});

	if (!response.ok) {
		const payload = (await response.json().catch(() => null)) as { error?: string } | null;
		throw new Error(payload?.error || "Failed to update shipping line");
	}

	return (await response.json()) as UpdateOrderShippingLineResult;
}

/**
 * Shim-only line item mutation for local editing flow.
 */
export async function shimUpdateOrderLineItemRequest(
	input: ShimLineItemUpdateInput,
): Promise<ShimLineItemUpdateResult> {
	await new Promise((resolve) => setTimeout(resolve, 250));

	return {
		ok: true,
		...input,
	};
}

/**
 * Hook for omnibox order searching.
 */
export function useOrderSearch(
	term: string,
	enabled: boolean,
): UseQueryResult<OrderSummary[], Error> {
	return useQuery({
		queryKey: orderKeys.search(term),
		queryFn: () => searchOrdersRequest(term),
		enabled,
		staleTime: 30 * 1000,
	});
}

/**
 * Hook for loading a single order.
 */
export function useOrder(id: string, enabled: boolean = true): UseQueryResult<OrderDetail, Error> {
	return useQuery({
		queryKey: orderKeys.detail(id),
		queryFn: () => fetchOrderRequest(id),
		enabled,
		staleTime: 60 * 1000,
	});
}

/**
 * Hook for persisting a line-item message change.
 */
export function useUpdateOrderMessage(): UseMutationResult<
	UpdateOrderMessageResult,
	Error,
	UpdateOrderMessageInput
> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateOrderMessageRequest,
		onSuccess: (_, variables) => {
			void queryClient.invalidateQueries({ queryKey: orderKeys.detail(variables.orderId) });
		},
	});
}

/**
 * Hook for persisting fulfillment date changes.
 */
export function useUpdateOrderFulfillmentDate(): UseMutationResult<
	UpdateOrderFulfillmentDateResult,
	Error,
	UpdateOrderFulfillmentDateInput
> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateOrderFulfillmentDateRequest,
		onSuccess: (_, variables) => {
			void queryClient.invalidateQueries({ queryKey: orderKeys.detail(variables.orderId) });
		},
	});
}

/**
 * Hook for persisting direct fulfillAt timestamp changes.
 */
export function useUpdateOrderFulfillAt(): UseMutationResult<
	UpdateOrderFulfillAtResult,
	Error,
	UpdateOrderFulfillAtInput
> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateOrderFulfillAtRequest,
		onSuccess: (_, variables) => {
			void queryClient.invalidateQueries({ queryKey: orderKeys.detail(variables.orderId) });
		},
	});
}

/**
 * Hook for persisting direct fulfillBy timestamp changes.
 */
export function useUpdateOrderFulfillBy(): UseMutationResult<
	UpdateOrderFulfillByResult,
	Error,
	UpdateOrderFulfillByInput
> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateOrderFulfillByRequest,
		onSuccess: (_, variables) => {
			void queryClient.invalidateQueries({ queryKey: orderKeys.detail(variables.orderId) });
		},
	});
}

/**
 * Hook for triggering scheduling workflow logic for one fulfillment order.
 */
export function useTriggerOrderScheduleWorkflow(): UseMutationResult<
	TriggerOrderScheduleWorkflowResult,
	Error,
	TriggerOrderScheduleWorkflowInput
> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: triggerOrderScheduleWorkflowRequest,
		onSuccess: (_, variables) => {
			void queryClient.invalidateQueries({ queryKey: orderKeys.detail(variables.orderId) });
		},
	});
}

export type ShipmentResponse = {
	shipment?: {
		shipment_id?: string;
		warehouse_id?: string;
		ship_to?: Record<string, string>;
	};
};

/**
 * Fetches a single ShipStation shipment by Shopify order number and fulfillment order numeric ID.
 */
export async function fetchShipmentRequest(
	orderNumber: string,
	fulfillmentOrderId: string,
): Promise<ShipmentResponse | null> {
	const response = await apiFetch(
		`/shipments/order/${encodeURIComponent(orderNumber)}/${encodeURIComponent(fulfillmentOrderId)}`,
	);
	if (!response.ok) return null;
	return (await response.json()) as ShipmentResponse;
}

/**
 * Hook for fetching ShipStation shipments for every fulfillment order in an order.
 * Queries are enabled only once the parent order query has loaded.
 * @param orderNumber - Shopify order name / numeric ID
 * @param numericFulfillmentOrderIds - Array of numeric fulfillment order IDs
 * @param enabled - Whether the queries should fire (pass `!isLoading && !error`)
 */
export function useOrderShipments(
	orderNumber: string,
	numericFulfillmentOrderIds: string[],
	enabled: boolean,
) {
	return useQueries({
		queries: numericFulfillmentOrderIds.map((fulfillmentOrderId) => ({
			queryKey: orderKeys.shipment(orderNumber, fulfillmentOrderId),
			queryFn: () => fetchShipmentRequest(orderNumber, fulfillmentOrderId),
			enabled: enabled && Boolean(orderNumber),
			staleTime: 60 * 1000,
		})),
	});
}

/**
 * Hook for toggling order shipping line edits.
 */
export function useUpdateOrderShippingLine(): UseMutationResult<
	UpdateOrderShippingLineResult,
	Error,
	UpdateOrderShippingLineInput
> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateOrderShippingLineRequest,
		onSuccess: (_, variables) => {
			void queryClient.invalidateQueries({ queryKey: orderKeys.detail(variables.orderId) });
		},
	});
}

/**
 * Hook for shimmed line-item update mutations.
 */
export function useShimUpdateOrderLineItem(): UseMutationResult<
	ShimLineItemUpdateResult,
	Error,
	ShimLineItemUpdateInput
> {
	return useMutation({
		mutationFn: shimUpdateOrderLineItemRequest,
	});
}
